import type { LookupItem } from '../mocks/criminalMasterData';
import { CIVIL_ADVISE_CATEGORY_CODE, CIVIL_ADVISE_CATEGORY_ID } from '../pages/wizard/civilAdvise/civilAdviseConstants';
import {
  CIVIL_APPELLATE_CATEGORY_CODE,
  CIVIL_APPELLATE_CATEGORY_ID,
  isCivilAppellateCategory,
} from '../pages/wizard/civilAppellate/civilAppellateConstants';
import {
  CIVIL_LITIGATION_CATEGORY_CODE,
  CIVIL_LITIGATION_CATEGORY_ID,
} from '../pages/wizard/civilLitigation/civilLitigationConstants';
import {
  CRIMINAL_APPELLATE_CATEGORY_CODE,
  CRIMINAL_APPELLATE_CATEGORY_ID,
} from '../pages/wizard/criminalAppellate/criminalAppellateConstants';

export function isOtherCorporateInstitute(
  id: string,
  name?: string,
  code?: string,
): boolean {
  if (code?.trim().toUpperCase() === 'OTHER') return true;
  if (id.trim().toLowerCase() === 'other') return true;
  return (name ?? '').trim().toLowerCase() === 'other';
}

export function selectedCorporateInstitute(
  corporateInstituteId: string,
  corporateInstitutes: LookupItem[],
): LookupItem | undefined {
  const id = corporateInstituteId.trim();
  if (!id) return undefined;
  return corporateInstitutes.find((c) => c.id === id);
}

export function isOtherCorporateInstituteSelected(
  corporateInstituteId: string,
  corporateInstitutes: LookupItem[],
  corporateInstituteName?: string,
): boolean {
  const item = selectedCorporateInstitute(corporateInstituteId, corporateInstitutes);
  return isOtherCorporateInstitute(
    corporateInstituteId,
    item?.name ?? corporateInstituteName,
    item?.code,
  );
}

/** File categories that show Institute Type — Civil Advise, Civil Litigation, Civil Appellate, and Criminal Appellate. */
export function categoryShowsInstituteType(
  fileCategoryId: string,
  fileCategories: LookupItem[],
): boolean {
  const id = fileCategoryId.trim();
  if (!id) return false;
  if (id === CIVIL_ADVISE_CATEGORY_ID || id === CIVIL_LITIGATION_CATEGORY_ID) return true;
  if (CIVIL_APPELLATE_CATEGORY_ID && id === CIVIL_APPELLATE_CATEGORY_ID) return true;
  if (id === CRIMINAL_APPELLATE_CATEGORY_ID) return true;
  const row = fileCategories.find((c) => c.id === id);
  if (row && isCivilAppellateCategory(row)) return true;
  const code = row?.code?.trim().toUpperCase() ?? '';
  return (
    code === CIVIL_ADVISE_CATEGORY_CODE ||
    code === CIVIL_LITIGATION_CATEGORY_CODE ||
    code === CIVIL_APPELLATE_CATEGORY_CODE ||
    code === CRIMINAL_APPELLATE_CATEGORY_CODE
  );
}

const GOVERNMENT_LOCKED_SUB_CODES = new Set(['CL', 'B']);
const CORPORATION_LOCKED_SUB_CODES = new Set(['BCL']);

export type InstituteTypeValue = 'government' | 'corporation';

export const INSTITUTE_TYPE_LABELS: Record<InstituteTypeValue, string> = {
  government: 'Government Department',
  corporation: 'Corporation',
};

export function subCategoryCode(
  subFileCategoryId: string,
  subCategories: LookupItem[],
): string {
  const id = subFileCategoryId.trim();
  if (!id) return '';
  return subCategories.find((s) => s.id === id)?.code?.trim().toUpperCase() ?? '';
}

export function subCategoryName(
  subFileCategoryId: string,
  subCategories: LookupItem[],
): string {
  const id = subFileCategoryId.trim();
  if (!id) return '';
  return subCategories.find((s) => s.id === id)?.name?.trim() ?? '';
}

/** Match Data Dictionary sub-category labels when API omits short codes (CL, B, BCL). */
function lockedInstituteTypeFromSubCategoryName(name: string): InstituteTypeValue | null {
  const n = name.trim().toUpperCase();
  if (!n) return null;
  if (n.startsWith('BCL') || /^BCL\s*[-–]/.test(n)) return 'corporation';
  if (n.startsWith('CL') || /^CL\s*[-–]/.test(n)) return 'government';
  if (n === 'B' || n === 'BILLS' || /^B\s*[-–]/.test(n) || /^BILLS\b/.test(n)) return 'government';
  return null;
}

export function categoryCode(
  fileCategoryId: string,
  fileCategories: LookupItem[],
): string {
  const id = fileCategoryId.trim();
  if (!id) return '';
  return fileCategories.find((c) => c.id === id)?.code?.trim().toUpperCase() ?? '';
}

/** Locked institute type for Civil Division - Advise sub-categories (CL, B, BCL). */
export function lockedInstituteTypeForCivilAdviseSubCategory(
  subCode: string,
  subName?: string,
): InstituteTypeValue | null {
  const code = subCode.trim().toUpperCase();
  if (GOVERNMENT_LOCKED_SUB_CODES.has(code)) return 'government';
  if (CORPORATION_LOCKED_SUB_CODES.has(code)) return 'corporation';
  return lockedInstituteTypeFromSubCategoryName(subName ?? '');
}

export function isInstituteTypeLocked(
  fileCategoryId: string,
  subFileCategoryId: string,
  fileCategories: LookupItem[],
  subCategories: LookupItem[],
): boolean {
  if (categoryCode(fileCategoryId, fileCategories) !== CIVIL_ADVISE_CATEGORY_CODE) return false;
  return (
    lockedInstituteTypeForCivilAdviseSubCategory(
      subCategoryCode(subFileCategoryId, subCategories),
      subCategoryName(subFileCategoryId, subCategories),
    ) !== null
  );
}

export function resolveInstituteTypeForSelection(
  fileCategoryId: string,
  subFileCategoryId: string,
  fileCategories: LookupItem[],
  subCategories: LookupItem[],
): InstituteTypeValue | '' {
  if (!categoryShowsInstituteType(fileCategoryId, fileCategories)) return '';
  if (categoryCode(fileCategoryId, fileCategories) !== CIVIL_ADVISE_CATEGORY_CODE) {
    return 'government';
  }
  const locked = lockedInstituteTypeForCivilAdviseSubCategory(
    subCategoryCode(subFileCategoryId, subCategories),
    subCategoryName(subFileCategoryId, subCategories),
  );
  return locked ?? 'government';
}

export type InstituteTypeDraftFields = {
  instituteType: InstituteTypeValue | '';
  corporateInstituteId: string;
  corporateInstituteName: string;
  corporateInstituteOther: string;
  corporateInstituteContactName: string;
  corporateInstitutePrefix: string;
};

export type InstituteFieldsPatch = {
  instituteType: InstituteTypeValue | '';
  corporateInstituteId: string;
  corporateInstituteName: string;
  corporateInstituteOther: string;
  corporateInstituteContactName: string;
  corporateInstitutePrefix: string;
};

export function clearedInstituteFields(): InstituteFieldsPatch {
  return {
    instituteType: '',
    corporateInstituteId: '',
    corporateInstituteName: '',
    corporateInstituteOther: '',
    corporateInstituteContactName: '',
    corporateInstitutePrefix: '',
  };
}

export function instituteFieldsForSubCategoryChange(
  fileCategoryId: string,
  subFileCategoryId: string,
  fileCategories: LookupItem[],
  subCategories: LookupItem[],
): InstituteFieldsPatch {
  if (!categoryShowsInstituteType(fileCategoryId, fileCategories)) {
    return clearedInstituteFields();
  }

  const instituteType = resolveInstituteTypeForSelection(
    fileCategoryId,
    subFileCategoryId,
    fileCategories,
    subCategories,
  );

  if (instituteType !== 'corporation') {
    return {
      instituteType,
      corporateInstituteId: '',
      corporateInstituteName: '',
      corporateInstituteOther: '',
      corporateInstituteContactName: '',
      corporateInstitutePrefix: '',
    };
  }

  return {
    instituteType,
    corporateInstituteId: '',
    corporateInstituteName: '',
    corporateInstituteOther: '',
    corporateInstituteContactName: '',
    corporateInstitutePrefix: '',
  };
}

export function instituteFieldsForFileCategoryChange(
  fileCategoryId: string,
  fileCategories: LookupItem[],
): InstituteFieldsPatch {
  if (!categoryShowsInstituteType(fileCategoryId, fileCategories)) {
    return clearedInstituteFields();
  }
  return {
    ...clearedInstituteFields(),
    instituteType: 'government',
  };
}
